import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Pencil, Check, X, Loader2, FileWarning, TrendingUp, TrendingDown, Wrench, ArrowLeftRight, PiggyBank, History,
  Users, Receipt, Coins,
} from 'lucide-react';
import { formatINR, formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';

// The six modules the counter reaches for most — same labels/icons as the
// sidebar (components/layout/nav.js), surfaced here too since the sidebar is
// long enough that these shouldn't require hunting through it every time.
const QUICK_ACCESS = [
  { to: '/customers', label: 'Customers', icon: Users, tone: 'text-info' },
  { to: '/invoices', label: 'Sales & GST invoices', icon: Receipt, tone: 'text-primary' },
  { to: '/gold-purchases', label: 'Gold purchase', icon: Coins, tone: 'text-warning' },
  { to: '/repairs', label: 'Repair vouchers', icon: Wrench, tone: 'text-success' },
  { to: '/issue-receipt', label: 'Issue & receipt', icon: ArrowLeftRight, tone: 'text-info' },
  { to: '/chit', label: 'Chit schemes', icon: PiggyBank, tone: 'text-warning' },
];

function QuickAccessBar() {
  return (
    <section>
      <h2 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-muted">Quick access</h2>
      <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-6">
        {QUICK_ACCESS.map(({ to, label, icon: Icon, tone }) => (
          <Link
            key={to}
            to={to}
            className="card flex flex-col items-center gap-2 p-3.5 text-center transition-colors hover:bg-surface-2"
          >
            <span className={`flex h-10 w-10 items-center justify-center rounded-full bg-surface-2 ${tone}`}>
              <Icon size={20} aria-hidden="true" />
            </span>
            <span className="text-xs font-medium leading-tight">{label}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

const ACTION_LABEL = {
  create: 'created', update: 'updated', cancel: 'cancelled', delete: 'deleted', delete_draft: 'deleted draft',
  save_draft: 'saved draft', issue: 'issued', pay: 'paid', refund: 'refunded', status_change: 'status changed',
};

function actionSummary(action) {
  const [, verb] = action.split('.');
  return ACTION_LABEL[verb] ?? verb ?? action;
}

function StatCard({ label, value, hint, tone = 'default' }) {
  return (
    <div className="rounded-card border border-border p-3.5">
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
      <p className={`tabular mt-1 text-xl font-semibold ${tone === 'warning' ? 'text-warning' : tone === 'danger' ? 'text-danger' : ''}`}>
        {value}
      </p>
      {hint ? <p className="mt-0.5 text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

/**
 * A correction to today's rate for one purity — used when the morning entry
 * was mistyped. This does not rewrite history: it inserts a new metal_rates
 * row (rows are never overwritten), so any invoice already billed keeps the
 * rate it actually used. Only new invoices from this point on see the
 * corrected figure.
 */
function RateCard({ purity, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: (ratePerGram) => api.post('/rates/today', { rates: [{ purityId: purity.purityId, ratePerGram }] }),
    onSuccess: () => {
      setEditing(false);
      onSaved();
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not save the correction.'),
  });

  const startEdit = () => {
    setValue(purity.ratePerGram ?? '');
    setError(null);
    setEditing(true);
  };

  const save = (e) => {
    e.preventDefault();
    const rate = Number(value);
    if (!(rate > 0)) {
      setError('Enter a rate greater than zero.');
      return;
    }
    setError(null);
    mutation.mutate(rate);
  };

  return (
    <div className="rounded-card border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-wide text-muted">
          {purity.metal} · {purity.label}
        </p>
        {!editing ? (
          <button
            type="button"
            onClick={startEdit}
            className="flex h-7 w-7 items-center justify-center rounded text-muted hover:bg-surface-2 hover:text-content"
            aria-label={`Correct today's ${purity.label} rate`}
            title="Correct today's rate"
          >
            <Pencil size={13} aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {editing ? (
        <form onSubmit={save} className="mt-1.5 flex items-center gap-1.5">
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-9 w-full min-w-0 rounded-card border border-border bg-surface px-2 text-sm tabular"
          />
          <button
            type="submit"
            disabled={mutation.isPending}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-card bg-primary text-primary-fg disabled:opacity-50"
            aria-label="Save correction"
          >
            {mutation.isPending ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Check size={15} aria-hidden="true" />}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-card border border-border text-muted"
            aria-label="Cancel"
          >
            <X size={15} aria-hidden="true" />
          </button>
        </form>
      ) : (
        <p className="tabular mt-1 text-lg font-semibold">
          {purity.ratePerGram ? formatINR(purity.ratePerGram) : '—'}
          <span className="text-sm font-normal text-muted"> /g</span>
        </p>
      )}

      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: rates } = useQuery({ queryKey: ['rates', 'today'], queryFn: () => api.get('/rates/today') });
  const { data: pendingEinvoices } = useQuery({ queryKey: ['einvoice', 'pending'], queryFn: () => api.get('/einvoice/pending') });
  const { data: dashboard } = useQuery({ queryKey: ['reports', 'dashboard'], queryFn: () => api.get('/reports/dashboard') });

  const refetchRates = () => queryClient.invalidateQueries({ queryKey: ['rates', 'today'] });

  const momChange = dashboard && Number(dashboard.lastMonthToDate.amount) > 0
    ? ((Number(dashboard.monthToDate.amount) - Number(dashboard.lastMonthToDate.amount)) / Number(dashboard.lastMonthToDate.amount)) * 100
    : null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Welcome</h1>
        <p className="text-sm text-muted">{user?.deviceLabel}</p>
      </div>

      <QuickAccessBar />

      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">Today&rsquo;s rates</h2>
        </div>
        <p className="mb-4 text-sm text-muted">
          Tap <Pencil size={11} className="inline align-baseline" aria-hidden="true" /> to correct a mistyped rate —
          invoices already billed keep the rate they used.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {rates?.purities?.map((p) => (
            <RateCard key={p.purityId} purity={p} onSaved={refetchRates} />
          ))}
        </div>
      </section>

      {pendingEinvoices?.length ? (
        <section className="card p-5">
          <div className="mb-1 flex items-center gap-2">
            <FileWarning size={18} className="text-warning" aria-hidden="true" />
            <h2 className="font-display text-lg font-semibold">Pending e-invoices</h2>
          </div>
          <p className="mb-3 text-sm text-muted">These B2B invoices haven&rsquo;t registered with the IRP yet.</p>
          <ul className="flex flex-col gap-1.5">
            {pendingEinvoices.map((row) => (
              <li key={row.id}>
                <Link to={`/invoices/${row.invoice_id}`} className="flex items-center justify-between rounded-card border border-border px-3 py-2 text-sm hover:bg-surface-2">
                  <span className="font-medium">{row.invoice_no}</span>
                  <span className="text-muted">{row.customer_name} · {row.status}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {dashboard ? (
        <>
          <section className="card p-5 sm:p-6">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">Today&rsquo;s sales</p>
                <p className="tabular mt-1.5 text-4xl font-semibold leading-none tracking-tight">
                  {formatINR(dashboard.today.salesAmount)}
                </p>
                <p className="mt-2 text-sm text-muted">{dashboard.today.salesCount} invoice(s) issued today</p>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:shrink-0">
                <StatCard label="Collections" value={formatINR(dashboard.today.collectionsAmount)} hint="Payments received" />
                <StatCard
                  label="Old gold bought"
                  value={formatINR(dashboard.today.goldPurchaseAmount)}
                  hint={`${dashboard.today.goldPurchaseCount} bill(s)`}
                />
              </div>
            </div>
          </section>

          <section className="card p-5">
            <h2 className="mb-3 font-display text-lg font-semibold">Month to date</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <StatCard label="This month" value={formatINR(dashboard.monthToDate.amount)} hint={`${dashboard.monthToDate.count} invoice(s)`} />
              <StatCard
                label="Same period last month"
                value={formatINR(dashboard.lastMonthToDate.amount)}
                hint={
                  momChange === null ? undefined : (
                    <span className={momChange >= 0 ? 'text-success' : 'text-danger'}>
                      {momChange >= 0 ? <TrendingUp size={11} className="inline" /> : <TrendingDown size={11} className="inline" />}{' '}
                      {Math.abs(momChange).toFixed(1)}% vs last month
                    </span>
                  )
                }
              />
            </div>
          </section>

          <section className="card p-5">
            <h2 className="mb-3 font-display text-lg font-semibold">Gold stock on hand</h2>
            {dashboard.goldStock.length ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {dashboard.goldStock.map((g) => (
                  <StatCard key={`${g.metal}-${g.purityId ?? 'none'}`} label={`${g.metal} · ${g.label}`} value={formatWeight(g.netWt)} hint={`Fine: ${formatWeight(g.fineWt)}`} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted">No stock movement recorded yet.</p>
            )}
          </section>

          <section className="card p-5">
            <h2 className="mb-3 font-display text-lg font-semibold">Needs attention</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Link to="/reports/repair-status" className="block">
                <StatCard
                  label="Repairs pending"
                  value={dashboard.repairs.pending}
                  hint={dashboard.repairs.overdue > 0 ? `${dashboard.repairs.overdue} overdue` : 'None overdue'}
                  tone={dashboard.repairs.overdue > 0 ? 'danger' : 'default'}
                />
              </Link>
              <Link to="/reports/pending-issues" className="block">
                <StatCard label="Issues outstanding" value={dashboard.issuesOutstanding} hint="With karigars/aasaris" />
              </Link>
              <Link to="/reports/chit-dues" className="block">
                <StatCard
                  label="Chit due this week"
                  value={dashboard.chitDue.count}
                  hint={formatINR(dashboard.chitDue.amount)}
                  tone={dashboard.chitDue.count > 0 ? 'warning' : 'default'}
                />
              </Link>
            </div>
          </section>

          <section className="card p-5">
            <div className="mb-3 flex items-center gap-2">
              <History size={16} className="text-muted" aria-hidden="true" />
              <h2 className="font-display text-lg font-semibold">Recent activity</h2>
            </div>
            {dashboard.recentActivity.length ? (
              <ul className="flex flex-col gap-2 text-sm">
                {dashboard.recentActivity.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-0 last:pb-0">
                    <span className="truncate">{a.summary ?? actionSummary(a.action)}</span>
                    <span className="shrink-0 text-xs text-muted">
                      {a.userName ?? 'System'} · {new Date(a.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted">Nothing yet today.</p>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
